import { CommonModule } from '@angular/common';
import { Component, ContentChild, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';
import { DefaultCancelButtonDirective, DefaultConfirmButtonDirective, OverlayGuardsDirective } from '@shared/directives/';
import { EditCreateFormComponent, FormConfig } from '@shared/components/edit-create-form';
import { PopoverConfig, PopoverHandlerConfig, PopoverAction } from './generic-popover.interfaces';
import { FormHandlerService } from '@shared/services';

@Component({
  selector: 'generic-popover',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PopoverModule,
    ButtonModule,
    EditCreateFormComponent,
    DefaultCancelButtonDirective,
    DefaultConfirmButtonDirective,
    OverlayGuardsDirective
  ],
  templateUrl: './generic-popover.component.html'
})
export class GenericPopoverComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() popoverConfig: PopoverConfig = { formConfig: { fields: [] }, actions: [], secondaryActions: [] };
  @Input() selectedData: any = null;
  @Input() editMode: boolean = false;

  @Output() onHide = new EventEmitter<void>();
  @Output() onSubmit = new EventEmitter<{ formValue: any }>();
  @Output() onFieldChange = new EventEmitter<{ fieldKey: string, value: any, formValue: any }>();

  // Template references for custom field templates
  @ContentChild('customFieldTemplate') customFieldTemplate?: TemplateRef<any>;
  @ViewChild('popover') popoverRef!: Popover;

  formConfig: FormConfig = { fields: [] };
  transformedInitialData: any = null;
  form: FormGroup | null = null; // Reference to the form from edit-create-form

  // Function map for action onClick handlers
  actionFunctionMap: Record<string, (popoverConfig: PopoverHandlerConfig, formValue: any) => void | Promise<any>> = {};

  // Built-in queuing mechanism for PrimeNG popover visibility bug workaround
  private queuedShow: { event?: Event, target?: any, selectedData?: any } | null = null;


  constructor(
    private formHandlerService: FormHandlerService
  ) {
    this.actionFunctionMap = {
      'cancel': (popoverHandlerConfig: PopoverHandlerConfig, formValue: any) => this.formHandlerService.handleCancel(popoverHandlerConfig),
      'save': (popoverHandlerConfig: PopoverHandlerConfig, formValue: any) => this.formHandlerService.handleSave({
        config: popoverHandlerConfig,
        formValue,
        editMode: this.editMode
      })
    };
  }

  ngOnInit() {
    this.loadFormConfig();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['popoverConfig'] && !changes['popoverConfig'].firstChange) {
      this.loadFormConfig();
    }
    if (changes['selectedData']) {
      this.transformInitialData();
    }
  }

  private loadFormConfig() {
    this.formConfig = this.popoverConfig.formConfig;
    this.transformInitialData();
  }

  private transformInitialData() {
    if (this.selectedData && this.popoverConfig.handlerConfig?.initialDataTransform) {
      this.transformedInitialData = this.popoverConfig.handlerConfig.initialDataTransform(this.selectedData);
    } else {
      this.transformedInitialData = this.selectedData;
    }
  }

  get popoverWidth(): string {
    return this.popoverConfig.width || '350px';
  }

  get popoverTitle(): string {
    if (this.editMode === true) {
      return `Chỉnh sửa ${this.popoverConfig.handlerConfig?.entityLabel || ''}`;
    }
    return this.popoverConfig.title || '';
  }

  onFormChange(form: FormGroup) {
    this.form = form;
    this.onFieldChange.emit({
      fieldKey: '',
      value: null,
      formValue: form.value
    });
  }

  /**
   * Enhanced show method with built-in queuing for PrimeNG popover visibility bug
   * If popover is already visible, queues the show request and hides current popover
   * @param event Optional event object
   * @param target Optional target element
   * @param selectedData Optional data to set (if different from current selectedData)
   */
  show(event?: Event, target?: any, selectedData?: any) {
    if (!this.popoverRef) return;

    // If selectedData is provided, update it
    if (selectedData !== undefined) {
      this.selectedData = selectedData;
    }

    // Check if popover is already visible (PrimeNG bug workaround)
    if (this.popoverRef.overlayVisible) {
      // Queue this show request
      this.queuedShow = { event, target, selectedData };
      // Hide current popover (this will trigger onPopoverHide which processes the queue)
      this.popoverRef.hide();
      return;
    }

    // Normal show behavior
    this.transformInitialData();
    this.popoverRef.show(event, target);
  }

  toggle(event?: Event, target?: any) {
    if (this.popoverRef) {
      this.transformInitialData();
      this.popoverRef.toggle(event, target);
    }
  }

  hide() {
    if (this.popoverRef) {
      this.popoverRef.hide();
    }
  }

  onPopoverHide() {
    // Emits the onHide event to notify parent components
    this.onHide.emit();


    // Handle queued show request (PrimeNG bug workaround)
    if (this.queuedShow) {
      const queued = this.queuedShow;
      this.queuedShow = null; // Clear queue first to prevent infinite loops

      // Use queueMicrotask for reliable timing after popover is fully hidden
      queueMicrotask(() => {
        this.show(queued.event, queued.target, queued.selectedData);
      });
    }
  }

  executeAction(action: PopoverAction) {
    if (!this.form) {
      console.error('Form not available');
      return;
    }
    let handlerConfig = this.popoverConfig.handlerConfig;
    if (!handlerConfig) {
      console.error('Handler configuration is required');
      return
    }

    // Check config-provided action handlers first, then fall back to built-in ones
    const configActionHandlers = this.popoverConfig.actionHandlers || {};
    const mergedActionMap = { ...this.actionFunctionMap, ...configActionHandlers };

    const actionFunction = mergedActionMap[action.onClick];
    if (actionFunction) {
      const result = actionFunction(handlerConfig, this.form.getRawValue());
      if (result instanceof Promise) {
        result.catch(error => {
          console.error('Action failed:', error);
        });
      }
    }
  }
}
